import React, { useEffect, useRef, useState } from "react";
import { Send, X, Sparkles, RotateCcw, Check } from "lucide-react";
import { mtFetch, MtLimitError, MtSessionError } from "@/lib/mtClient";

export interface ChipsItem {
    order: number;
    text: string;
    applied: boolean;
}

export interface Message {
    role: "user" | "assistant";
    content: string;
    chips?: ChipsItem[];
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    sourceText: string;
    targetText: string;
    sourceLang: string;
    targetLang: string;
    projectName: string;
    projectId?: string;
    allSegments: Array<{
        id: string;
        segment_order: number;
        source_text: string;
        target_text: string;
    }>;
    messages: Message[];
    setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
    onApplySegmentEdit?: (segmentOrder: number, newText: string) => void;
    onApplyComplete?: () => void;
}

const SYSTEM_PROMPT = (
    projectName: string,
    sourceLang: string,
    targetLang: string,
    segmentCount: number,
    respondInTarget: boolean = false,
) => `You are TranslyxAI, an expert translation assistant embedded in TranslyxCAT — a professional Computer-Assisted Translation tool.

${
    respondInTarget
        ? `MANDATORY LANGUAGE RULE: You MUST respond ONLY in ${targetLang}. Every single word of your response must be in ${targetLang}. Do not use English at all. Not even partially. This is non-negotiable.`
        : `Respond in English only.`
}

You are helping a professional translator working on:
- Project: "${projectName}"
- Language pair: ${sourceLang} → ${targetLang}
- Document: ${segmentCount} segments

Your role is to:
1. Help with translation decisions — suggest translations, explain nuances, handle ambiguity
2. Provide cultural and linguistic context specific to the target language variant
3. Flag terminology inconsistencies and suggest corrections
4. Explain grammar rules when asked
5. Help with domain-specific terminology (legal, medical, technical, etc.)
6. Review translations for accuracy, tone, and register
7. Answer questions about the source text meaning

RESPONSE STYLE — CRITICAL:
- Be extremely concise. 2-4 sentences maximum for most answers.
- Never use bullet points unless the user explicitly asks for a list.
- Never provide background/context unless asked.
- Get straight to the answer — no preamble, no "Great question!", no summaries.
- Only elaborate when the user asks "explain" or "why" or requests detail.
- For translation suggestions: give the suggestion first, one-line reason second.
- Never be condescending — this is a professional translator, not a student.
- When discussing Portuguese, ALWAYS specify pt-PT or pt-BR. The project target is ${targetLang} — if it's pt-PT, use European Portuguese conventions.

APPLY CAPABILITY: You CAN apply changes directly to segments. When suggesting
replacements, ALWAYS format each one on its own line as:
[Segment N] → "corrected text"
Use straight double quotes exactly as shown. The translator will see an
"Apply" button appear automatically for each suggestion in this format.
Never tell the user they must apply changes manually — you have direct
apply capability via this format.`;

// Welcome message is always English — AI responses follow the language toggle
const getWelcomeMessage = (
    projectName: string,
    sourceLang: string,
    targetLang: string,
    respondInTarget: boolean,
): Message => ({
    role: "assistant",
    content: respondInTarget
        ? `Hi! I'm **TranslyxAI**, your translation assistant.\n\nTracking **${projectName}** (${sourceLang} → ${targetLang}).\n\nI'll respond in **${targetLang}**. How can I help?`
        : `Hi! I'm **TranslyxAI**, your translation assistant.\n\nI can see you're working on **${projectName}** (${sourceLang} → ${targetLang}).\n\nHow can I help? You can ask me to:\n- Suggest or review a translation\n- Explain a term or phrase\n- Check terminology consistency\n- Help with cultural nuances`,
});

// Quick prompts are always English — they just fill the input box,
// the AI response language is controlled by the system prompt toggle
const QUICK_PROMPTS = [
    "Review my translation",
    "Suggest alternatives",
    "Explain this term",
    "Check the register",
];

export const TranslyxAIPanel: React.FC<Props> = ({
    isOpen,
    onClose,
    sourceText,
    targetText,
    sourceLang,
    targetLang,
    projectName,
    projectId,
    allSegments,
    messages,
    setMessages,
    onApplySegmentEdit,
    onApplyComplete,
}) => {
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [pendingChips, setPendingChips] = useState<
        Array<{ order: number; text: string; applied: boolean }>
    >([]);
    const abortControllerRef = useRef<AbortController | null>(null);
    const [respondInTarget, setRespondInTarget] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Per-project localStorage key — prevents message bleed across projects
    const storageKey = projectId
        ? `translyx_ai_messages_${projectId}`
        : `translyx_ai_messages_${projectName.replace(/\s+/g, "_")}`;

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // Persist messages to localStorage keyed by project
    useEffect(() => {
        if (messages.length > 0) {
            localStorage.setItem(storageKey, JSON.stringify(messages));
        }
    }, [messages, storageKey]);

    // On open: load this project's messages and restore chip state, or show fresh welcome
    useEffect(() => {
        if (!isOpen) return;
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    setMessages(parsed);
                    // Restore pendingChips from the last assistant message with chips
                    for (let i = parsed.length - 1; i >= 0; i--) {
                        const m = parsed[i] as Message;
                        if (m.role === "assistant" && m.chips) {
                            setPendingChips(m.chips);
                            break;
                        }
                    }
                    return;
                }
            }
        } catch {
            /* ignore */
        }
        setMessages([
            getWelcomeMessage(
                projectName,
                sourceLang,
                targetLang,
                respondInTarget,
            ),
        ]);
    }, [isOpen, storageKey]);

    // Update welcome message when language toggle changes (only if it's still the only message)
    useEffect(() => {
        setMessages((prev) => {
            if (prev.length === 1 && prev[0].role === "assistant") {
                return [
                    getWelcomeMessage(
                        projectName,
                        sourceLang,
                        targetLang,
                        respondInTarget,
                    ),
                ];
            }
            return prev;
        });
    }, [respondInTarget]);

    const sendMessage = async () => {
        const text = input.trim();
        if (!text || loading) return;

        const userMsg: Message = { role: "user", content: text };
        const newMessages = [...messages, userMsg];
        setMessages(newMessages);
        setInput("");
        setLoading(true);
        setPendingChips([]);

        const needsSegmentContext =
            /find|search|replace|all instance|segment|translat|consistenc|comissão|comité|committee/i.test(
                text,
            );
        const abortController = new AbortController();
        abortControllerRef.current = abortController;
        const timeoutId = setTimeout(
            () => abortController.abort(),
            needsSegmentContext ? 45000 : 15000,
        );

        try {
            const contextNote = sourceText
                ? `\n\n[Current segment]\nSource: "${sourceText}"\nTranslation: "${targetText || "(not yet translated)"}"`
                : "";

            const needsSegmentContext =
                /find|search|replace|all instance|segment|translat|consistenc|comissão|comité|committee/i.test(
                    text,
                );

            const segmentContext = needsSegmentContext
                ? "\n\nPROJECT SEGMENTS (first 200):\n" +
                  allSegments
                      .slice(0, 200)
                      .map(
                          (s) =>
                              `[Segment ${s.segment_order}] SOURCE: ${s.source_text} | TARGET: ${s.target_text || "(untranslated)"}`,
                      )
                      .join("\n") +
                  '\n\nWhen suggesting replacements format each as: [Segment N] → "corrected text"'
                : "";

            const fullSystem =
                SYSTEM_PROMPT(
                    projectName,
                    sourceLang,
                    targetLang,
                    allSegments.length,
                    respondInTarget,
                ) +
                contextNote +
                segmentContext;

            const response = await mtFetch(
                `${(import.meta as any).env?.VITE_API_URL || "https://api.linguaproz.com"}/api/translate/ai`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    signal: abortController.signal,
                    body: JSON.stringify({
                        system: fullSystem,
                        messages: newMessages.map((m) => ({
                            role: m.role,
                            content: m.content,
                        })),
                        max_tokens: 1024,
                        tier: "standard",
                    }),
                },
            );

            const data = await response.json();
            const reply =
                data.content?.[0]?.text ||
                "Sorry, I could not generate a response.";
            const chipPattern =
                /\[Segment (\d+)\]\s*[→\-]+\s*["(]?([^"\n)]+)[")]?/g;
            const chips: Array<{
                order: number;
                text: string;
                applied: boolean;
            }> = [];
            let match;
            while ((match = chipPattern.exec(reply)) !== null) {
                chips.push({
                    order: Number(match[1]),
                    text: match[2],
                    applied: false,
                });
            }
            setMessages((prev) => [
                ...prev,
                chips.length > 0
                    ? { role: "assistant", content: reply, chips }
                    : { role: "assistant", content: reply },
            ]);
            if (chips.length > 0) setPendingChips(chips);
        } catch (error: any) {
            if (error.name === "AbortError") {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            "⏱ Request timed out. Try asking about fewer segments at a time.",
                    },
                ]);
            } else if (
                error instanceof MtLimitError ||
                error instanceof MtSessionError
            ) {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            error instanceof MtLimitError
                                ? "You have used all your AI queries for this month. Upgrade your plan on the Plan & billing page to keep going."
                                : error.message,
                    },
                ]);
            } else {
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content: "⚠️ Connection error. Please try again.",
                    },
                ]);
            }
        } finally {
            clearTimeout(timeoutId);
            abortControllerRef.current = null;
            setLoading(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    const clearChat = () => {
        localStorage.removeItem(storageKey);
        setMessages([
            getWelcomeMessage(
                projectName,
                sourceLang,
                targetLang,
                respondInTarget,
            ),
        ]);
    };

    if (!isOpen) return null;

    // Display label for the language toggle button
    const toggleLabel = respondInTarget
        ? targetLang.split("-")[0].toUpperCase() // 'fr', 'pt-PT' → 'FR', 'PT'
        : "EN";

    return (
        <div className="fixed top-0 right-0 bottom-0 w-96 bg-white border-l border-gray-200 shadow-2xl z-40 flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700 bg-slate-900 shrink-0">
                <div className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-gold-500" />
                    <div>
                        <h3 className="text-sm font-bold text-gold-400">
                            TranslyxAI
                        </h3>
                        <p className="text-xs font-semibold text-slate-100">
                            Translation Assistant
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => setRespondInTarget((r) => !r)}
                        title={
                            respondInTarget
                                ? `Responding in ${targetLang} — click for English`
                                : `Click to respond in ${targetLang}`
                        }
                        className={`px-2 py-1 rounded text-xs font-medium transition-colors ${
                            respondInTarget
                                ? "bg-gold-500 text-white"
                                : "bg-slate-700 text-slate-200 hover:text-white"
                        }`}
                    >
                        {toggleLabel}
                    </button>
                    <button
                        onClick={clearChat}
                        title="Clear conversation"
                        className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-200 hover:text-white"
                    >
                        <RotateCcw className="w-4 h-4" />
                    </button>
                    <button
                        onClick={onClose}
                        className="p-1.5 hover:bg-slate-700 rounded-lg transition-colors text-slate-200 hover:text-white"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Context bar */}
            {sourceText && (
                <div className="px-3 py-2 bg-slate-800 border-b border-slate-700 shrink-0">
                    <p className="text-xs font-bold text-slate-100 mb-0.5 uppercase tracking-wide">
                        Current segment
                    </p>
                    <p className="text-xs font-medium text-slate-200 line-clamp-1 italic">
                        "{sourceText}"
                    </p>
                </div>
            )}

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-3 space-y-4">
                {messages.map((msg, idx) => (
                    <div
                        key={idx}
                        className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}
                    >
                        {msg.role === "assistant" && (
                            <div className="w-6 h-6 rounded-full bg-gold-600 flex items-center justify-center shrink-0 mr-2 mt-0.5">
                                <Sparkles className="w-3.5 h-3.5 text-white" />
                            </div>
                        )}
                        <div
                            className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                                msg.role === "user"
                                    ? "bg-gold-600 text-white rounded-br-sm"
                                    : "bg-gray-100 text-gray-800 rounded-bl-sm"
                            }`}
                        >
                            {msg.content.split("\n").map((line, i) => {
                                const formatted = line.replace(
                                    /\*\*(.+?)\*\*/g,
                                    "<strong>$1</strong>",
                                );
                                return (
                                    <p
                                        key={i}
                                        className={i > 0 ? "mt-1" : ""}
                                        dangerouslySetInnerHTML={{
                                            __html: formatted,
                                        }}
                                    />
                                );
                            })}
                        </div>
                    </div>
                ))}
                {loading && (
                    <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-gold-600 flex items-center justify-center shrink-0">
                            <Sparkles className="w-3.5 h-3.5 text-white" />
                        </div>
                        <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-3 py-2">
                            <div className="flex gap-1">
                                <span
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: "0ms" }}
                                />
                                <span
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: "150ms" }}
                                />
                                <span
                                    className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                                    style={{ animationDelay: "300ms" }}
                                />
                            </div>
                        </div>
                    </div>
                )}
                {pendingChips.length > 0 && (
                    <div className="space-y-1.5 pt-1">
                        {(() => {
                            const unapplied = pendingChips.filter(
                                (c) => !c.applied,
                            );
                            return (
                                unapplied.length > 1 && (
                                    <button
                                        onClick={() => {
                                            unapplied.forEach((chip) => {
                                                onApplySegmentEdit?.(
                                                    chip.order,
                                                    chip.text,
                                                );
                                            });
                                            onApplyComplete?.();
                                            const appliedSet = new Set(
                                                pendingChips.map(
                                                    (c) => c.order,
                                                ),
                                            );
                                            setMessages((prev) => {
                                                const updated = prev.map(
                                                    (m) => ({
                                                        ...m,
                                                        chips: m.chips?.map(
                                                            (c) =>
                                                                appliedSet.has(
                                                                    c.order,
                                                                )
                                                                    ? {
                                                                          ...c,
                                                                          applied: true,
                                                                      }
                                                                    : c,
                                                        ),
                                                    }),
                                                );
                                                localStorage.setItem(
                                                    storageKey,
                                                    JSON.stringify(updated),
                                                );
                                                return updated;
                                            });
                                            setPendingChips((prev) =>
                                                prev.map((c) => ({
                                                    ...c,
                                                    applied: true,
                                                })),
                                            );
                                        }}
                                        className="w-full text-xs font-semibold py-1.5 px-3 rounded mb-1"
                                        style={{
                                            background: "#d4a017",
                                            color: "#0f172a",
                                        }}
                                    >
                                        ✓ Apply All ({unapplied.length} changes)
                                    </button>
                                )
                            );
                        })()}
                        {pendingChips.map((chip, i) => (
                            <button
                                key={i}
                                disabled={chip.applied}
                                onClick={() => {
                                    onApplySegmentEdit?.(chip.order, chip.text);
                                    setPendingChips((prev) =>
                                        prev.map((c, ci) =>
                                            ci === i
                                                ? { ...c, applied: true }
                                                : c,
                                        ),
                                    );
                                    setMessages((prev) => {
                                        const updated = prev.map((m) => ({
                                            ...m,
                                            chips: m.chips?.map((c) =>
                                                c.order === chip.order
                                                    ? { ...c, applied: true }
                                                    : c,
                                            ),
                                        }));
                                        localStorage.setItem(
                                            storageKey,
                                            JSON.stringify(updated),
                                        );
                                        return updated;
                                    });
                                }}
                                className={`flex items-center gap-2 w-full text-left px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                    chip.applied
                                        ? "bg-slate-700 text-slate-400 cursor-default"
                                        : "bg-[#d4a017] text-slate-900 hover:bg-gold-400"
                                }`}
                            >
                                <Check className="w-3.5 h-3.5 shrink-0" />
                                {chip.applied
                                    ? `Applied ✓ — Segment ${chip.order}`
                                    : `Apply: Segment ${chip.order} → "${chip.text}"`}
                            </button>
                        ))}
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Quick prompts — always English, AI response language set by toggle */}
            {messages.length <= 2 && sourceText && (
                <div className="px-3 py-2 border-t border-gray-100 flex gap-1.5 flex-wrap shrink-0">
                    {QUICK_PROMPTS.map((prompt) => (
                        <button
                            key={prompt}
                            onClick={() => {
                                setInput(prompt);
                                inputRef.current?.focus();
                            }}
                            className="text-xs px-2.5 py-1 rounded-full bg-gray-100 hover:bg-gold-50 hover:text-gold-700 text-gray-700 border border-gray-200 hover:border-gold-300 transition-colors"
                        >
                            {prompt}
                        </button>
                    ))}
                </div>
            )}

            {/* Input */}
            <div className="px-3 py-3 border-t border-slate-700 bg-slate-900 shrink-0">
                <div className="flex gap-2 items-end">
                    <textarea
                        ref={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask TranslyxAI anything…"
                        rows={1}
                        className="flex-1 px-3 py-2 border border-slate-500 rounded-xl text-sm focus:outline-none focus:border-gold-400 resize-none min-h-[38px] max-h-32 bg-slate-800 text-white placeholder-slate-400"
                        style={{ height: "auto" }}
                        onInput={(e) => {
                            const t = e.target as HTMLTextAreaElement;
                            t.style.height = "auto";
                            t.style.height =
                                Math.min(t.scrollHeight, 128) + "px";
                        }}
                    />
                    {loading && (
                        <button
                            onClick={() => abortControllerRef.current?.abort()}
                            className="px-2.5 py-2 bg-slate-800 border border-gold-500/60 text-gold-400 hover:text-gold-300 hover:border-gold-400 rounded-xl text-xs font-medium transition-colors shrink-0"
                        >
                            Cancel
                        </button>
                    )}
                    <button
                        onClick={sendMessage}
                        disabled={!input.trim() || loading}
                        className="p-2 bg-gold-500 hover:bg-gold-400 disabled:bg-slate-700 disabled:text-slate-100 text-white rounded-xl transition-colors shrink-0"
                    >
                        <Send className="w-4 h-4" />
                    </button>
                </div>
                <p className="text-xs font-medium text-slate-200 mt-1.5 text-center">
                    Enter to send · Shift+Enter for new line
                </p>
            </div>
        </div>
    );
};
